import React, { useEffect, useRef, useState } from 'react';

import { Alert, Button, Flex, Grid, Group, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconCheck, IconX } from '@tabler/icons-react';

import {
  CreateAppointmentResponse,
  useCreateAppointmentMutation
} from '@/store/slices/api/schedules/schedulesApiSlice';
import { getTomorrow } from '@/utils/constants';
import formatDate from '@/utils/formatDate';
import DatePicker from '@/components/DatePickers/DatePicker';

import Dropdown from '@/ui/Dropdown';

import styles from '../DeviceScheduleModal.module.scss';
import SchedulesSummary from '../SchedulesSummary';
import { EditScheduleStepProps } from '../types';

const BillableSchedule: React.FC<EditScheduleStepProps> = ({
  onBack,
  onClose,
  onSuccess,
  loading,
  isAppointmentManagedInRs,
  setLoading,
  patientSFId,
  deviceInfo
}) => {
  const [showSummary, setShowSummary] = useState(false);
  const [apiResponse, setApiResponse] = useState<CreateAppointmentResponse | null>(null);
  const [scheduledDate, setScheduledDate] = useState<Date | null>(null);
  const [appointmentType, setAppointmentType] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const hasAutoCreated = useRef(false);

  const [createAppointment] = useCreateAppointmentMutation();

  useEffect(() => {
    // Auto-create when NOT managed in RS
    if (!isAppointmentManagedInRs && !hasAutoCreated.current) {
      hasAutoCreated.current = true;
      setAppointmentType('Routine');
      handleAutoCreate();
    }
  }, [isAppointmentManagedInRs]);

  const handleAutoCreate = async () => {
    if (!patientSFId || !deviceInfo) {
      return;
    }

    setLoading(true);
    setErrorMessage(null);

    try {
      const tomorrow = getTomorrow();
      const formattedDate = formatDate(tomorrow, 'YYYY-MM-DD');

      const appointmentData = {
        patientSFId: patientSFId,
        scheduleDate: formattedDate,
        type: 'Routine' as any,
        modelNumber: deviceInfo.modelNumber,
        serialNumber: deviceInfo.serialNumber
      };

      const result = await createAppointment(appointmentData).unwrap();

      if (!result.success) {
        console.error('API returned success: false', result);
        const errMsg = result.message || 'Failed to create Routine appointment';
        setErrorMessage(errMsg);
        return;
      }

      setApiResponse(result);
      setShowSummary(true);
      console.log('Setting showSummary to true, apiResponse:', result);
    } catch (error) {
      console.error('Error creating billable appointment:', error);

      let errMsg = 'Failed to create Routine appointment';

      if (error && typeof error === 'object' && 'data' in error) {
        const errorData = error.data as CreateAppointmentResponse;
        if (errorData && !errorData.success && errorData.message) {
          errMsg = errorData.message;
        }
      }

      setErrorMessage(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleManualSave = async () => {
    if (!appointmentType || !scheduledDate || !patientSFId || !deviceInfo) {
      return;
    }

    setLoading(true);
    setErrorMessage(null);

    try {
      const formattedDate = formatDate(scheduledDate, 'YYYY-MM-DD');

      const appointmentData = {
        patientSFId: patientSFId,
        scheduleDate: formattedDate,
        type: appointmentType as any,
        modelNumber: deviceInfo.modelNumber,
        serialNumber: deviceInfo.serialNumber
      };

      const result = await createAppointment(appointmentData).unwrap();

      if (!result.success) {
        console.error('API returned success: false', result);
        const errMsg = result.message || `Failed to create ${appointmentType} appointment`;
        setErrorMessage(errMsg);
        notifications.show({
          title: 'Error',
          message: errMsg,
          color: 'red',
          icon: <IconX size="1.1rem" />,
          autoClose: 5000
        });
        return;
      }

      notifications.show({
        title: 'Success',
        message: result.message || `${appointmentType} appointment created successfully`,
        color: 'green',
        icon: <IconCheck size="1.1rem" />,
        autoClose: 3000
      });

      setApiResponse(result);
      setShowSummary(true);
    } catch (error) {
      console.error('Error creating billable appointment:', error);

      let errMsg = `Failed to create ${appointmentType} appointment`;

      if (error && typeof error === 'object' && 'data' in error) {
        const errorData = error.data as CreateAppointmentResponse;
        if (errorData && !errorData.success && errorData.message) {
          errMsg = errorData.message;
        }
      }

      setErrorMessage(errMsg);
      notifications.show({
        title: 'Error',
        message: errMsg,
        color: 'red',
        icon: <IconX size="1.1rem" />,
        autoClose: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShowSummary(false);
    setApiResponse(null);
    setScheduledDate(null);
    setAppointmentType(null);
    setErrorMessage(null);

    if (apiResponse?.success) {
      onSuccess?.();
    }

    onClose();
  };

  if (!isAppointmentManagedInRs) {
    if (showSummary) {
      return (
        <SchedulesSummary
          appointmentType="Routine"
          nextDates={apiResponse?.summary?.nextDates}
          onClose={handleClose}
        />
      );
    }

    // Show loading or error state while auto-creating
    return (
      <Stack gap="md" px={20} pt={16} align="center" justify="center" style={{ minHeight: 200 }}>
        {errorMessage ? (
          <>
            <Alert icon={<IconAlertCircle size="1rem" />} title="Error" color="red" w="100%">
              {errorMessage}
            </Alert>
            <Button onClick={handleClose} color="plum.9">
              Close
            </Button>
          </>
        ) : (
          <Text size="sm" c="plum.7" fw={500}>
            Creating appointment...
          </Text>
        )}
      </Stack>
    );
  }

  // Manual flow (when managed in RS) - show summary if available
  if (showSummary && appointmentType) {
    return (
      <SchedulesSummary
        appointmentType={appointmentType}
        nextDates={apiResponse?.summary?.nextDates}
        onClose={handleClose}
      />
    );
  }

  // Show the form
  return (
    <Stack gap={'xs'}>
      <Flex px={20} pt={16} direction={'column'}>
        <Title order={5} fw={600} mb={'xs'} c={'plum.9'}>
          Create Billable Schedule
        </Title>
        <Text size="xs" c={'plum'} mb={8}>
          Set Billable appointment for patient, which will automatically adjust appointments
          schedules.
        </Text>
        <Grid columns={6} mt={8}>
          <Grid.Col span={3}>
            <Text size="xs" c={'plum'} mb={4} fw={500}>
              Appointment Type
            </Text>
            <Dropdown
              options={[
                { label: 'Routine', value: 'Routine' },
                { label: 'Heart Failure', value: 'Heart Failure' },
                { label: 'Combo', value: 'Combo' }
              ]}
              onChange={(option) => {
                if (option?.value) {
                  setAppointmentType(option.value);
                }
              }}
              isMulti={false}
              placeholder="Select Appointment Type"
              isDisabled={loading}
            />
          </Grid.Col>
          <Grid.Col span={3}>
            <Text size="xs" c={'plum'} mb={4} fw={500}>
              Scheduled Date
            </Text>
            <DatePicker
              inputProps={{
                placeholder: 'MM/DD/YYYY',
                required: true,
                disabled: loading
              }}
              datePickerProps={{
                selected: scheduledDate,
                onChange: setScheduledDate,
                isClearable: false,
                minDate: getTomorrow(),
                dateFormat: 'MM/dd/yyyy',
                disabled: loading
              }}
            />
          </Grid.Col>
        </Grid>
      </Flex>
      <Group className={styles.buttonGroup} justify="flex-end">
        <Button
          variant="outline"
          color="plum.9"
          onClick={onBack}
          className={styles.modalButton}
          disabled={loading}>
          Back
        </Button>
        <Button
          color="plum.9"
          onClick={handleManualSave}
          className={styles.modalButton}
          disabled={!appointmentType || !scheduledDate || loading}
          loading={loading}>
          Save
        </Button>
      </Group>
    </Stack>
  );
};

export default BillableSchedule;
